import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu01Icon } from '@untitledui-icons/react/line'
import { useUIStore } from '@/stores/uiStore'
import { Logo } from './Logo'
import { cn } from '@/lib/utils'

interface HeaderProps {
  className?: string
}

/**
 * Mobile header component - only shown on small screens
 */
export function Header({ className }: HeaderProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const toggleMobileMenu = useUIStore((state) => state.toggleMobileMenu)

  // Get page title from current path
  const getPageTitle = (path: string) => {
    const segment = path.split('/')[1] || 'dashboard'
    // Try to use translation key first
    const key = `navigation.${segment.replace('-', '_')}`
    const translated = t(key, { defaultValue: '' })
    if (translated && translated !== key) {
      return translated
    }
    // Fallback to formatted segment
    return segment
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const title = getPageTitle(location.pathname)

  return (
    <header
      className={cn(
        'sticky top-0 z-40 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4',
        'dark:border-gray-800 dark:bg-gray-900',
        'lg:hidden',
        className
      )}
    >
      {/* Menu button */}
      <button
        type="button"
        onClick={toggleMobileMenu}
        className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
        aria-label={t('components.header.open_menu')}
      >
        <Menu01Icon className="h-6 w-6" />
      </button>

      {/* Title */}
      <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
        {title}
      </h1>

      {/* Logo icon */}
      <div className="flex h-10 w-10 items-center justify-center">
        <Logo iconOnly width={32} />
      </div>
    </header>
  )
}

export default Header
